// Per-tenant third-party tracking.
//
// One build serves N merchant domains, so the vendor snippets cannot be baked
// into index.html. index.html ships only the vendors' *queue stubs*; this
// module appends the real scripts once GET /storefront/shop has told us which
// ids this tenant uses (see fetchShopProfile in terbebasApi.ts). Calls made
// before that — e.g. a ViewContent on a deep-linked product page — sit in the
// stub queues and flush when the vendor script loads, so nothing is lost.
//
// Config shape and id formats live in common-ts/models/StoreAnalytics, mirrored
// server-side in terbebas-backend src/service/analytics.service.ts.

import {
  ANALYTICS_VENDORS,
  StoreAnalyticsConfig,
  analyticsVendorId,
  isAnalyticsVendorActive,
} from 'common-ts/dist/models/StoreAnalytics';

let initialized = false;
let activePixelId: string | null = null;
let activeGa4Id: string | null = null;

// The Meta pixel id this storefront is reporting to, or null when the merchant
// has no pixel configured. AccountRedux uses it to re-init with advanced
// matching on sign-out.
export function getActivePixelId(): string | null {
  return activePixelId;
}

// Real shopper traffic only. The prodpanel LayoutEditor renders the storefront
// in an iframe to preview homepage blocks; tracking that would pollute the
// merchant's own funnel with their editing session. /link (link-in-bio) is a
// real public page, so only /preview-blocks is excluded by path.
function isTrackableContext(): boolean {
  try {
    if (window.self !== window.top) return false;
  } catch {
    // Cross-origin parent — still an embed, still not shopper traffic.
    return false;
  }
  return window.location.pathname !== '/preview-blocks';
}

function appendScript(src: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function initMetaPixel(pixelId: string): void {
  const w = window as any;
  activePixelId = pixelId;
  // init/track before the script lands is the vendor's own pattern: these
  // queue on the stub and replay once fbevents.js defines callMethod.
  w.fbq('init', pixelId);
  w.fbq('track', 'PageView');
  appendScript('https://connect.facebook.net/en_US/fbevents.js');
}

function initHotjar(siteId: string): void {
  const w = window as any;
  w._hjSettings = { hjid: Number(siteId), hjsv: 6 };
  // sv=6 tracks SPA route changes on its own — no trackPageView hook needed.
  appendScript(`https://static.hotjar.com/c/hotjar-${encodeURIComponent(siteId)}.js?sv=6`);
}

function initGtm(containerId: string): void {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  // The <noscript> iframe half of the GTM snippet is omitted: this is a
  // JS-only SPA, so a no-JS visitor never renders anything to track.
  appendScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`);
}

function initGa4(measurementId: string): void {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== 'function') {
    // gtag must push the arguments object itself, not an array copy.
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer.push(arguments);
    };
  }
  appendScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`);
  w.gtag('js', new Date());
  w.gtag('config', measurementId);
  activeGa4Id = measurementId;
}

function initClarity(projectId: string): void {
  appendScript(`https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`);
}

// A stub whose vendor never loads would buffer every call for the life of the
// session. Retire the unused ones instead. fbq goes back to null because the
// legacy call sites guard on `fbq !== null`.
function retireUnusedStubs(activeKeys: Set<string>): void {
  const w = window as any;
  if (!activeKeys.has('metaPixel')) {
    w.fbq = null;
  }
  if (!activeKeys.has('hotjar')) {
    w.hj = function () { /* no tracker configured */ };
  }
  if (!activeKeys.has('clarity')) {
    w.clarity = function () { /* no tracker configured */ };
  }
}

// Loads whichever trackers this tenant has enabled. Idempotent — the boot
// profile fetch calls it once, and a second call is a no-op.
export function initAnalytics(config?: StoreAnalyticsConfig): void {
  if (initialized) return;
  initialized = true;

  // isAnalyticsVendorActive re-checks each id against its format before we
  // interpolate it into a URL, so a malformed row written before the server
  // validated ids can never reach the DOM.
  const active = isTrackableContext()
    ? ANALYTICS_VENDORS.filter((spec) => isAnalyticsVendorActive(config, spec))
    : [];

  active.forEach((spec) => {
    const id = analyticsVendorId(config, spec);
    switch (spec.key) {
      case 'metaPixel': initMetaPixel(id); break;
      case 'hotjar': initHotjar(id); break;
      case 'gtm': initGtm(id); break;
      case 'ga4': initGa4(id); break;
      case 'clarity': initClarity(id); break;
    }
  });

  retireUnusedStubs(new Set(active.map((spec) => spec.key)));
}

// Meta Pixel and GA4 do not observe SPA navigation; without this only the
// landing page is counted. Called by AnalyticsRouteTracker on route change.
// Hotjar (sv=6), GTM and Clarity handle history changes themselves.
export function trackPageView(): void {
  const w = window as any;
  if (activePixelId && typeof w.fbq === 'function') {
    w.fbq('track', 'PageView');
  }
  if (activeGa4Id && typeof w.gtag === 'function') {
    w.gtag('event', 'page_view', {
      page_path: window.location.pathname + window.location.search,
    });
  }
}
